import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  COST_RANGE_CAPTION,
  Modal,
  Spinner,
  US_STATES,
  buttonClasses,
  cn,
  costRange,
  fmtUsd,
  nextYearMarker,
  type CostRange,
  type CostToolEstimate,
  type CostToolEstimateDrug,
  type CostToolPlanOption,
} from "@dearbill/shared";
import { InfoCircleIcon } from "@dearbill/shared/components/icons";
import { ReferSomeoneModal, type ReferEstimateContext } from "../../shell/ReferSomeoneModal";
import personIcon from "../../assets/icon-person.svg";
import printerIcon from "../../assets/icon-printer.svg";

export interface ResultScreenProps {
  orgId: string;
  /** The plan the answer is for; the context row names it. */
  plan: CostToolPlanOption;
  /** Question 1's state code, for the context row; "" renders no state. */
  stateCode: string;
  /** How many medications the answer covers, for the context row. */
  drugCount: number;
  /** The last resolved estimate; null while the first one is in flight. */
  estimate: CostToolEstimate | null;
  /**
   * The shown estimate answered a different input set than the current one:
   * the plan, drugs or quantities changed since it landed. While a refresh is
   * in flight that is the normal dimmed update. Combined with `error`, it means
   * the refresh failed and the old numbers must not read as the current
   * answer, so the ghost tiles render instead.
   */
  stale: boolean;
  /** True while an estimate is in flight; it dims the previous numbers but never blanks them. */
  loading: boolean;
  /** The estimate call failed (nothing stale to show or refresh failed). */
  error: boolean;
  /**
   * At least one priced row still uses the engine-assumed per-fill quantity, so
   * the fine print invites the tap-to-edit. Once every priced row is
   * partner-set, the invitation drops.
   */
  anyAssumed: boolean;
  onRetry: () => void;
  /**
   * The current estimate's inputs, present only when a live estimate is on
   * screen. Passed to the refer modal so an email send attaches the cost-report
   * PDF; absent means the plain link. It carries only the plan and drugs,
   * never a patient field, so the referral stays anonymous.
   */
  estimateContext?: ReferEstimateContext | undefined;
  /** Reset the calculator for the next patient; the parent owns what resets. */
  onCheckAnother: () => void;
  /** The form screen's path, for the back link. */
  backTo: string;
}

type BannerTone = "success" | "info" | "warning";

/** Medicare's own M3P page, linked from the explainer modal's footer. */
const MEDICARE_M3P_URL = "https://www.medicare.gov/prescription-payment-plan";

/**
 * The helps headline: the M3P phrase styled as interactive with an info icon,
 * opening the plain-language explainer modal. The phrase and icon are one
 * button so a tap anywhere on either opens it; the modal copy avoids "save"
 * and "savings" like the rest of the page.
 */
function M3pHeadline(): ReactNode {
  const [open, setOpen] = useState(false);
  return (
    <>
      Your patient would benefit from the{" "}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Medicare Prescription Payment Plan (M3P) — what is it?"
        className="hover:text-ember inline cursor-pointer text-left underline underline-offset-2"
      >
        Medicare Prescription Payment Plan (M3P)
        <InfoCircleIcon size={18} className="mb-1 ml-1.5 inline-block align-middle" />
      </button>
      <Modal
        open={open}
        title="What is the Medicare Prescription Payment Plan?"
        onClose={() => setOpen(false)}
        closeOnBackdrop
        size="lg"
      >
        <div className="text-foreground flex flex-col gap-3 font-sans text-base font-normal leading-6">
          <p>
            The Medicare Prescription Payment Plan (M3P) is a Medicare program, available since 2025
            under the Inflation Reduction Act. It lets anyone with Medicare drug coverage (Part D)
            spread their out-of-pocket drug costs across the calendar year instead of paying large
            amounts at the pharmacy counter.
          </p>
          <p>
            The yearly total stays the same — the plan changes <em>when</em> they pay, not how much.
            There is no interest, no fee, and no credit check.
          </p>
          <p>
            Once enrolled, the member pays $0 at the pharmacy counter for covered drugs. Their Part
            D plan sends them a monthly bill instead.
          </p>
          <p>
            Anyone with Part D coverage can opt in. It tends to help most when drug costs are large
            early in the year.
          </p>
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <a
            href={MEDICARE_M3P_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-ember hover:text-ember-dark font-semibold underline underline-offset-2"
          >
            More at medicare.gov
          </a>
          <Button variant="secondary" size="md" onClick={() => setOpen(false)}>
            Close
          </Button>
        </div>
      </Modal>
    </>
  );
}

/**
 * Verdict banner copy, written to be read aloud at the counter (the helps
 * headline is the design's, node 2340:259). M3P changes when a member pays,
 * not how much, so "save" and "savings" must never appear here or anywhere
 * else on this page; "benefit" refers to the payment timing, never a lower
 * total.
 */
const VERDICTS: Record<
  "helps" | "no_help" | "unknown",
  { tone: BannerTone; title: ReactNode; body: string }
> = {
  helps: {
    tone: "success",
    title: <M3pHeadline />,
    body: "The Medicare Prescription Payment Plan can smooth this into predictable monthly amounts. DearBill will take it from here — they'll hear from us.",
  },
  no_help: {
    tone: "info",
    title: "M3P wouldn't change much here",
    body: "This one wouldn't help them — costs are already low and steady. Refer them anyway and we'll check what else does.",
  },
  unknown: {
    tone: "warning",
    title: "We can't estimate this one",
    body: "We couldn't price these medications on this plan. You can still refer them — we'll work it out with the member directly.",
  },
};

/**
 * Reason-specific "unknown" copy, because an unpriceable plan is a different
 * conversation from an unpriceable drug and the pharmacist relays it. Other
 * reasons fall back to the generic unknown copy.
 */
const UNKNOWN_BY_REASON: Record<string, { tone: BannerTone; title: string; body: string }> = {
  plan_unknown: {
    tone: "warning",
    title: "We don't have drug pricing for this plan",
    body: "Some plans — like PACE and employer group plans — don't publish standard Part D pricing, so we can't run the numbers here. You can still refer them — we'll work out costs with the member directly.",
  },
  no_drugs: {
    tone: "warning",
    title: "None of these medications are on this plan's list",
    body: "This plan's medication list doesn't include what you entered. You can still refer them — checking alternatives and coverage is exactly what we do.",
  },
};

// The success pair is the design's result banner (node 2340:258): an Ice fill
// with the muted green laurel border and deep-navy body text.
const BANNER_TONES: Record<BannerTone, { container: string; body: string }> = {
  success: { container: "bg-ice border-laurel", body: "text-deep-navy" },
  info: { container: "bg-ice border-haze", body: "text-deep-navy" },
  warning: { container: "bg-lemon border-warning-border", body: "text-ochre" },
};

/** Whole-dollar tile figure, because the counter conversation rounds to the dollar. */
const wholeUsd = (dollars: number): string => fmtUsd(Math.round(dollars));

/**
 * Rounded-dollar estimate ("est. $123"), a range ("est. $124–$224") when the
 * deductible-met pass is lower, or an em-dash when unknown.
 */
function estUsd(min: number | null | undefined, max: number | null): CostRange {
  if (max === null) return { text: "—", ranged: false };
  const range = costRange(min, max, wholeUsd);
  return { text: `est. ${range.text}`, ranged: range.ranged };
}

/**
 * Null-safe money cell: an em-dash for unknowns, or a range in cents when the
 * deductible-met pass is lower.
 */
function usdCell(min: number | null, max: number | null): CostRange {
  return costRange(min, max, (dollars) => fmtUsd(dollars, true));
}

/** The fine-print caption's gate: any detail-table cost / monthly cell reads as a range. */
function anyCellRanged(drugs: CostToolEstimateDrug[]): boolean {
  return drugs.some(
    (d) => usdCell(d.costMin, d.cost).ranged || usdCell(d.monthlyMin, d.monthly).ranged,
  );
}

/** "Texas", or null for an unknown or empty code. */
function stateName(code: string): string | null {
  return US_STATES.find((s) => s.code === code)?.name ?? null;
}

/**
 * The answer screen (`/orgs/:orgId/cost-tool/result`; Figma
 * dR8cJ6hRSW5eERToo64boe, node 2340:232).
 *
 * Top to bottom: the "Change the answers" back link beside a context row
 * naming the plan, state, medication count and plan year the answer is for;
 * the verdict banner with the read-aloud headline, the two comparison tiles
 * and, while both numbers are priced, the timing footer; the always-open
 * per-medication detail table; the actions row (refer, print, check another
 * patient); and the fine print. "Without M3P" is the biggest single month at
 * the counter; "With M3P" is the level monthly amount over the months left in
 * the year. The framing is monthly against upfront, never a discount.
 *
 * A next-year estimate (`pricesEstimated`) reads "<year> (est.)" in the
 * context row, and the banner and the detail table each carry the
 * `nextYearMarker` line. The marker prints with the answer, because the
 * printout is what the patient takes home.
 *
 * The parent (`CostToolTab`) owns every input and the estimate; this screen
 * only renders them, so the back link is a plain navigation and nothing is
 * lost between the screens. A refresh (the pharmacist went back, edited, and
 * ran again before the debounce settled) dims the previous numbers under a
 * small spinner instead of blanking them.
 *
 * The tiles and the table's cost and monthly columns read as a deductible-met
 * to unmet range ("est. $124–$224") whenever the bounds still differ after the
 * surface's rounding; the "Deductible portion" column stays the unmet pass's
 * slice. One caption in the fine print names what each bound assumes, only
 * while a range is on screen.
 *
 * Results are tied to their exact inputs. When the inputs changed and the
 * refresh failed (`stale && error`), the previous numbers answer a different
 * question, and showing them would let a pharmacist quote figures for the
 * wrong plan or drugs, so the ghost tiles and the error alert render instead.
 * A failure with unchanged inputs keeps the old, still valid numbers visible
 * beside the alert.
 *
 * "Print for patient" prints this page; the back link and the actions row hide
 * themselves via `print:hidden`, so the printout is the answer, not the chrome
 * around it.
 */
export function ResultScreen({
  orgId,
  plan,
  stateCode,
  drugCount,
  estimate,
  stale,
  loading,
  error,
  anyAssumed,
  onRetry,
  estimateContext,
  onCheckAnother,
  backTo,
}: ResultScreenProps): ReactNode {
  const [referOpen, setReferOpen] = useState(false);
  // The failed refresh was for different inputs, so the held estimate no longer
  // answers what's on screen and must not render as the current answer.
  const staleFailure = error && stale && estimate !== null;
  const showEstimate = estimate !== null && !staleFailure;
  // Ghost tiles hold the layout while there is no showable answer: the first
  // estimate still in flight, a failure, or the stale-failure state.
  const showGhost = !loading && (estimate === null ? !error : staleFailure);
  const biggest = estUsd(estimate?.biggestMonthMin, estimate?.biggestMonth ?? null);
  const monthly = estUsd(estimate?.monthlyWithM3pMin, estimate?.monthlyWithM3p ?? null);
  const ranged =
    biggest.ranged || monthly.ranged || (showEstimate && anyCellRanged(estimate.drugs));
  const state = stateName(stateCode);
  // The year the shown answer is for, from the estimate itself; a next-year
  // answer is marked as the estimate it is.
  const yearLabel =
    showEstimate && estimate !== null && estimate.contractYear !== null
      ? `${estimate.contractYear}${estimate.pricesEstimated ? " (est.)" : ""}`
      : null;
  const estimatedYear =
    showEstimate && estimate !== null && estimate.pricesEstimated ? estimate.contractYear : null;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 print:hidden">
        <Link
          to={backTo}
          className="text-ember hover:text-ember-dark inline-flex min-h-11 items-center font-semibold underline underline-offset-2"
        >
          ← Change the answers
        </Link>
        <p className="text-slate-teal text-base leading-[22px]">
          <span className="text-ember font-semibold">{plan.planName}</span>
          {state !== null && <> · {state}</>} · {drugCount}{" "}
          {drugCount === 1 ? "medication" : "medications"}
          {yearLabel !== null && <> · {yearLabel}</>}
        </p>
      </div>

      {showGhost && (
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <GhostTile label="Without M3P" />
          <GhostTile label="With M3P" />
        </div>
      )}

      {error && (
        <Alert tone="error" title="We couldn't run the estimate." className="mt-5">
          <div className="mt-2">
            <Button variant="outlined" size="sm" onClick={onRetry}>
              Retry
            </Button>
          </div>
        </Alert>
      )}

      {estimate === null && loading && (
        <div className="text-slate-teal mt-5 flex items-center gap-3">
          <Spinner /> <span>Estimating…</span>
        </div>
      )}

      {showEstimate && estimate !== null && (
        <div className={cn("mt-5 transition-opacity", loading && "opacity-50")} aria-busy={loading}>
          <VerdictBanner estimate={estimate} biggest={biggest} monthly={monthly} />
          {loading && (
            <div className="text-slate-teal mt-2 flex items-center gap-2 text-xs">
              <Spinner size="sm" /> <span>Updating estimate…</span>
            </div>
          )}

          <DetailTable
            drugs={estimate.drugs}
            drivingDrugName={estimate.drivingDrug?.drugName ?? null}
            estimatedYear={estimatedYear}
          />
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center justify-center gap-3 print:hidden">
        <button
          type="button"
          onClick={() => setReferOpen(true)}
          className={buttonClasses("primary", "md", "gap-2 text-[17px]")}
        >
          <img src={personIcon} alt="" className="size-4 shrink-0" />
          Refer patient to DearBill
        </button>
        <button
          type="button"
          onClick={() => window.print()}
          className={buttonClasses("outlined", "md", "gap-2 text-[17px]")}
        >
          <img src={printerIcon} alt="" className="size-4 shrink-0" />
          Print for patient
        </button>
        {/* A neutral-bordered button (node 2340:316), deliberately not the
            ember `outlined` variant so it doesn't read as a twin of "Print
            for patient" beside it. */}
        <button
          type="button"
          onClick={onCheckAnother}
          className="border-border-strong text-foreground hover:bg-ice inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-lg border bg-white px-6 py-4 text-[17px] font-semibold leading-[1.5] transition active:scale-[0.97]"
        >
          Check another patient
        </button>
      </div>

      {showEstimate && estimate !== null && (
        <>
          <p className="text-slate-teal mt-5 text-sm leading-[22px]">
            Estimates only, not a quote — actual amounts are set by the plan at the pharmacy.{" "}
            {anyAssumed
              ? "Assumes 30-day fills at retail pharmacy pricing — tap a medication's quantity on the previous screen to match what's actually dispensed."
              : "Assumes 30-day fills at retail pharmacy pricing."}{" "}
            M3P changes when you pay, not how much you pay in a year.
          </p>
          {ranged && (
            <p className="text-slate-teal mt-1 text-sm leading-[22px]">{COST_RANGE_CAPTION}</p>
          )}
          {/* The estimate endpoint takes no deductible input
              (`CostToolEstimateRequest` carries only the plan, rxcuis and
              quantities), so this input stays disabled. It renders only while a
              range is on screen, because a known deductible would collapse it. */}
          {ranged && (
            <div className="mt-3 flex flex-wrap items-center gap-2 print:hidden">
              <label htmlFor="cost-tool-deductible-remaining" className="text-slate-teal text-sm">
                Deductible remaining (optional)
              </label>
              <input
                id="cost-tool-deductible-remaining"
                type="number"
                disabled
                aria-describedby="cost-tool-deductible-remaining-hint"
                className="border-haze text-slate-teal bg-ice h-9 w-24 rounded-md border px-2 text-sm"
              />
              <span id="cost-tool-deductible-remaining-hint" className="text-slate-teal text-xs">
                Not available yet. If we knew this, the range would narrow to one number.
              </span>
            </div>
          )}
        </>
      )}

      <ReferSomeoneModal
        open={referOpen}
        onClose={() => setReferOpen(false)}
        orgId={orgId}
        estimateContext={estimateContext}
      />
    </div>
  );
}

/** Placeholder twin of a comparison tile, with an em-dash where the number goes. */
function GhostTile({ label }: { label: string }): ReactNode {
  return (
    <div className="border-border-strong rounded-lg border-2 border-dashed p-5">
      <p className="text-slate-teal font-medium leading-[22px]">{label}</p>
      <p className="text-slate-teal mt-1 text-4xl font-bold leading-[54px]" aria-hidden="true">
        —
      </p>
    </div>
  );
}

/**
 * The verdict banner: the read-aloud headline, the verdict body, the two
 * comparison tiles and, only while both numbers are priced, the timing footer.
 * The framing is always one upfront bill without M3P against a level monthly
 * amount with it, never a discount or a lower total.
 */
function VerdictBanner({
  estimate,
  biggest,
  monthly,
}: {
  estimate: CostToolEstimate;
  biggest: CostRange;
  monthly: CostRange;
}): ReactNode {
  // Anything the backend couldn't price reads as the warning verdict, whatever
  // the verdict field says, because no numbers back a stronger claim.
  const unpriced = !estimate.coverageKnown || estimate.unavailableReason !== null;
  const v = unpriced
    ? (UNKNOWN_BY_REASON[estimate.unavailableReason ?? ""] ?? VERDICTS.unknown)
    : VERDICTS[estimate.verdict];
  const tone = BANNER_TONES[v.tone];
  const priced = estimate.biggestMonth !== null && estimate.monthlyWithM3p !== null;
  const monthsLeft = estimate.monthsRemaining;

  return (
    <div className={cn("rounded-lg border p-6", tone.container)}>
      <h3 className="text-deep-navy font-serif text-2xl font-bold leading-8">{v.title}</h3>
      <p className={cn("mt-2 leading-6", tone.body)}>{v.body}</p>
      {estimate.pricesEstimated && estimate.contractYear !== null && (
        <p className={cn("mt-2 text-sm leading-5", tone.body)}>
          {nextYearMarker(estimate.contractYear)}
        </p>
      )}

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <StatTile
          label="Without M3P"
          value={biggest.text}
          caption="At the counter, in their biggest month"
        />
        <StatTile
          label="With M3P"
          value={monthly.text}
          unit="/month"
          caption={`Spread over the ${monthsLeft} ${monthsLeft === 1 ? "month" : "months"} left in the year`}
        />
      </div>

      {priced && (
        <p className={cn("mt-4 font-semibold leading-6", tone.body)}>
          The year costs them the same either way. The plan changes when they pay, not how much.
          There is no interest and no fee.
        </p>
      )}
    </div>
  );
}

function StatTile({
  label,
  value,
  unit,
  caption,
}: {
  label: string;
  value: string;
  unit?: string;
  caption: string;
}): ReactNode {
  // Outfit Bold figures per the design's comparison tiles (node 2340:261),
  // not the serif text-num-lg recipe other SPAs keep for financial figures.
  return (
    <div className="border-border-strong rounded-lg border bg-white p-5">
      <p className="text-slate-teal font-medium leading-[22px]">{label}</p>
      <p className="text-foreground mt-1 flex items-end gap-1 font-sans text-[40px] font-bold leading-[48px]">
        <span>{value}</span>
        {unit !== undefined && (
          <span className="text-slate-teal pb-1 text-lg font-medium leading-7">{unit}</span>
        )}
      </p>
      <p className="text-slate-teal mt-1 text-[15px] leading-[22px]">{caption}</p>
    </div>
  );
}

/**
 * The always-open per-medication detail card: a header naming the driving
 * medication and the money table beneath it (the design's "Medication detail",
 * node 2340:273). `estimatedYear` is the contract year of a next-year
 * estimate, which adds the marker line under the table; null for this year's
 * prices.
 */
function DetailTable({
  drugs,
  drivingDrugName,
  estimatedYear,
}: {
  drugs: CostToolEstimateDrug[];
  drivingDrugName: string | null;
  estimatedYear: number | null;
}): ReactNode {
  return (
    <section
      aria-labelledby="cost-tool-detail-heading"
      className="border-border-strong mt-5 overflow-hidden rounded-lg border bg-white"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-4">
        <h3
          id="cost-tool-detail-heading"
          className="text-foreground font-serif text-[21px] font-semibold leading-7"
        >
          Medication detail
        </h3>
        {drivingDrugName !== null && (
          <p className="text-slate-teal text-[15px] leading-[22px]">
            Driven mostly by{" "}
            <span className="text-foreground font-semibold">{drivingDrugName}</span>
          </p>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-deep-navy">
            <tr className="text-[11px] font-semibold uppercase leading-[18px] tracking-[1.1px] text-white">
              <th className="px-4 py-3 font-semibold">Medication</th>
              <th className="px-4 py-3 font-semibold">Tier</th>
              <th className="px-4 py-3 font-semibold">Plan price</th>
              <th className="px-4 py-3 font-semibold">Deductible portion</th>
              <th className="px-4 py-3 font-semibold">Your cost per fill</th>
              <th className="px-4 py-3 font-semibold">Monthly share</th>
            </tr>
          </thead>
          <tbody>
            {drugs.map((d) => (
              <tr key={d.rxcui} className="border-haze border-t">
                <td className="text-foreground px-4 py-3 text-sm font-semibold">
                  {d.drugName ?? d.rxcui}
                  {d.label !== null && d.label.trim() !== "" && (
                    <span className="text-slate-teal ml-1.5 text-xs font-normal">{d.label}</span>
                  )}
                </td>
                {d.covered ? (
                  <>
                    <td className="text-slate-teal px-4 py-3 text-sm">{d.tier ?? "—"}</td>
                    <td className="text-slate-teal px-4 py-3 text-sm">
                      {usdCell(null, d.gross).text}
                    </td>
                    <td className="text-slate-teal px-4 py-3 text-sm">
                      {usdCell(null, d.deductiblePaid).text}
                    </td>
                    <td className="text-foreground px-4 py-3 text-sm font-semibold">
                      {usdCell(d.costMin, d.cost).text}
                    </td>
                    <td className="text-slate-teal px-4 py-3 text-sm">
                      {usdCell(d.monthlyMin, d.monthly).text}
                    </td>
                  </>
                ) : (
                  <td colSpan={5} className="text-slate-teal px-4 py-3 text-sm">
                    Not on this plan&rsquo;s list
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {estimatedYear !== null && (
        <p className="border-haze text-slate-teal border-t px-4 py-3 text-sm leading-5">
          {nextYearMarker(estimatedYear)}
        </p>
      )}
    </section>
  );
}
